import { lazy, Suspense } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { AppShell } from "@/app/AppShell";
import { RouteGuard } from "@/app/RouteGuard";
import { mainRoutes } from "@/app/routes";
import { LoadingState } from "@/shared/ui/LoadingState";
// Landing stays eager - it's the public home route, no point round-tripping
// a chunk request to render an iframe shell.
import { LandingPage } from "@/pages/LandingPage";

// Everything else: lazy so the landing bundle doesn't pull in auth, legal,
// extension, and onboarding code on first paint.
const AccessCodesPage = lazy(() =>
  import("@/pages/AccessCodesPage").then((m) => ({ default: m.AccessCodesPage })),
);
const AuthEntryPage = lazy(() =>
  import("@/pages/AuthEntryPage").then((m) => ({ default: m.AuthEntryPage })),
);
const CareersPage = lazy(() =>
  import("@/pages/CareersPage").then((m) => ({ default: m.CareersPage })),
);
const ConnectorPage = lazy(() =>
  import("@/pages/ConnectorPage").then((m) => ({ default: m.ConnectorPage })),
);
const ExtensionHandshakePage = lazy(() =>
  import("@/pages/ExtensionHandshakePage").then((m) => ({ default: m.ExtensionHandshakePage })),
);
const FeedbackAlumPage = lazy(() =>
  import("@/pages/FeedbackAlumPage").then((m) => ({ default: m.FeedbackAlumPage })),
);
const FeedbackStudentPage = lazy(() =>
  import("@/pages/FeedbackStudentPage").then((m) => ({ default: m.FeedbackStudentPage })),
);
const ScheduleAlumniPage = lazy(() =>
  import("@/pages/ScheduleAlumniPage").then((m) => ({ default: m.ScheduleAlumniPage })),
);
const ScheduleStudentPage = lazy(() =>
  import("@/pages/ScheduleStudentPage").then((m) => ({ default: m.ScheduleStudentPage })),
);
const NetworkReviewRedirectPage = lazy(() =>
  import("@/pages/NetworkReviewRedirectPage").then((m) => ({ default: m.NetworkReviewRedirectPage })),
);
const NotFoundPage = lazy(() =>
  import("@/pages/NotFoundPage").then((m) => ({ default: m.NotFoundPage })),
);
const OnboardingPage = lazy(() =>
  import("@/pages/OnboardingPage").then((m) => ({ default: m.OnboardingPage })),
);
const ReferencesPage = lazy(() =>
  import("@/pages/ReferencesPage").then((m) => ({ default: m.ReferencesPage })),
);
const ReferencesVerifyPage = lazy(() =>
  import("@/pages/ReferencesVerifyPage").then((m) => ({ default: m.ReferencesVerifyPage })),
);
const MatchesPage = lazy(() =>
  import("@/pages/MatchesPage").then((m) => ({ default: m.MatchesPage })),
);
const PathwaySignupPage = lazy(() =>
  import("@/pages/PathwaySignupPage").then((m) => ({ default: m.PathwaySignupPage })),
);
const PasswordRecoveryPage = lazy(() =>
  import("@/pages/PasswordRecoveryPage").then((m) => ({ default: m.PasswordRecoveryPage })),
);
const PrivacyPage = lazy(() =>
  import("@/pages/PrivacyPage").then((m) => ({ default: m.PrivacyPage })),
);
const DesignSystemPage = lazy(() =>
  import("@/pages/DesignSystemPage").then((m) => ({ default: m.DesignSystemPage })),
);
const TermsPage = lazy(() =>
  import("@/pages/TermsPage").then((m) => ({ default: m.TermsPage })),
);
const QuestionsPage = lazy(() =>
  import("@/pages/QuestionsPage").then((m) => ({ default: m.QuestionsPage })),
);
const VerifyEmailPage = lazy(() =>
  import("@/pages/VerifyEmailPage").then((m) => ({ default: m.VerifyEmailPage })),
);

// Note: previously wrapped Routes with withSentryReactRouterV6Routing to stamp
// per-route transaction names. Dropped to keep @sentry/react out of the main
// bundle (now lazy-loaded). Sentry traces will show raw URLs instead of route
// patterns - fine for this app since routes have no dynamic segments outside
// auth flow.

/** Old alumni links carry ?from= attribution, so the query must survive. */
function RedirectToConnector() {
  const { search } = useLocation();
  return <Navigate to={`/connector${search}`} replace />;
}

export function AppRouter() {
  return (
    <BrowserRouter future={{ v7_relativeSplatPath: true, v7_startTransition: true }}>
      <Suspense fallback={<LoadingState />}>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/signup" element={<AuthEntryPage />} />
        <Route path="/signup/pathway" element={<PathwaySignupPage />} />
        <Route path="/login" element={<AuthEntryPage />} />
        <Route path="/waitlist" element={<AuthEntryPage />} />
        <Route path="/forgot-password" element={<PasswordRecoveryPage />} />
        <Route path="/reset-password" element={<PasswordRecoveryPage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/careers" element={<CareersPage />} />
        <Route path="/questions" element={<QuestionsPage />} />
        {/* Connector opt-in. /alumni and /smu/alumni keep old links working. */}
        <Route path="/connector" element={<ConnectorPage />} />
        <Route path="/alumni" element={<RedirectToConnector />} />
        <Route path="/smu/alumni" element={<RedirectToConnector />} />
        {/* Retired: former Students Sheet intake. Seekers go signup → V3. */}
        <Route path="/apply" element={<Navigate to="/signup" replace />} />
        <Route path="/feedback/alum" element={<FeedbackAlumPage />} />
        <Route path="/feedback/student" element={<FeedbackStudentPage />} />
        {/* Intro scheduling — public token links, sheet-backed, no login. */}
        <Route path="/schedule/alumni" element={<ScheduleAlumniPage />} />
        <Route path="/schedule/student" element={<ScheduleStudentPage />} />
        <Route path="/references/verify" element={<ReferencesVerifyPage />} />
        <Route
          path="/onboarding"
          element={
            <RouteGuard requiresAuth>
              <OnboardingPage />
            </RouteGuard>
          }
        />
        <Route
          path="/references"
          element={
            <RouteGuard requiresAuth>
              <ReferencesPage />
            </RouteGuard>
          }
        />
        <Route
          path="/matches"
          element={
            <RouteGuard requiresAuth>
              <MatchesPage />
            </RouteGuard>
          }
        />
        <Route
          path="/extension/handshake"
          element={
            <RouteGuard requiresAuth>
              <ExtensionHandshakePage />
            </RouteGuard>
          }
        />
        {/* Legacy redirect - LinkedIn/Canvas content scripts deep-link here. */}
        <Route path="/network/review" element={<NetworkReviewRedirectPage />} />

        <Route element={<AppShell />}>
          {mainRoutes.map((route) => (
            <Route
              key={route.path}
              path={route.path}
              element={
                <RouteGuard requiresAuth={route.requiresAuth}>
                  {route.element}
                </RouteGuard>
              }
            />
          ))}
          <Route
            path="/me/access-codes"
            element={
              <RouteGuard requiresAuth>
                <AccessCodesPage />
              </RouteGuard>
            }
          />
          <Route
            path="/design-system"
            element={
              <RouteGuard requiresAuth>
                <DesignSystemPage />
              </RouteGuard>
            }
          />
        </Route>

        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      </Suspense>
    </BrowserRouter>
  );
}
